import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllPayments } from '../../services/api';
import Alert from '../../components/common/Alert';

function AdminBillingHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [filters, setFilters] = useState({
    status: '',
    payment_type: '',
    transaction_id: '',
    user_email: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const data = await getAllPayments();
        console.log('Admin billing data:', data); // Debug log
        const paymentsList = Array.isArray(data) ? data : (data.results || data);
        setPayments(Array.isArray(paymentsList) ? paymentsList : []);
      } catch (error) {
        console.error('Error fetching admin payment history:', error);
        setError('Failed to load admin billing history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const toggleExpanded = (paymentId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(paymentId)) {
      newExpanded.delete(paymentId);
    } else {
      newExpanded.add(paymentId);
    }
    setExpandedItems(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentTypeDisplay = (type) => {
    switch (type?.toLowerCase()) {
      case 'membership':
        return 'Membership';
      case 'event':
        return 'Event Registration';
      case 'donation':
        return 'Donation';
      default:
        return type || 'Payment';
    }
  };

  const filteredPayments = payments.filter(payment => {
    const paymentDate = new Date(payment.payment_date || payment.created_at);
    const fromDate = filters.date_from ? new Date(filters.date_from) : null;
    const toDate = filters.date_to ? new Date(filters.date_to + 'T23:59:59') : null;

    return (
      (!filters.status || payment.status === filters.status) &&
      (!filters.payment_type || payment.payment_type === filters.payment_type) &&
      (!filters.transaction_id || payment.transaction_id?.toLowerCase().includes(filters.transaction_id.toLowerCase())) &&
      (!filters.user_email || payment.user_email?.toLowerCase().includes(filters.user_email.toLowerCase())) &&
      (!fromDate || paymentDate >= fromDate) &&
      (!toDate || paymentDate <= toDate)
    );
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      payment_type: '',
      transaction_id: '',
      user_email: '',
      date_from: '',
      date_to: ''
    });
  };

  const totalAmount = filteredPayments
    .filter(p => p.status === 'completed')
    .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin - All Billing History</h1>
              <p className="mt-1 text-gray-600">
                View and manage all user payments and invoices
              </p>
            </div>
            <Link
              to="/admin/events"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Admin
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Payments</dt>
                    <dd className="text-lg font-medium text-gray-900">{filteredPayments.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                    <dd className="text-lg font-medium text-gray-900">{formatAmount(totalAmount)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Unique Users</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {new Set(filteredPayments.map(p => p.user_email)).size}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.081 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Failed Payments</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {filteredPayments.filter(p => p.status === 'failed').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filters & Search</h3>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Payment Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
                <option value="expired">Expired</option>
                <option value="disputed">Disputed</option>
              </select>
            </div>

            {/* Payment Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
              <select
                value={filters.payment_type}
                onChange={(e) => handleFilterChange('payment_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                <option value="membership">Membership</option>
                <option value="event">Event</option>
                <option value="donation">Donation</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Transaction ID Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Transaction ID</label>
              <input
                type="text"
                value={filters.transaction_id}
                onChange={(e) => handleFilterChange('transaction_id', e.target.value)}
                placeholder="Search by transaction ID..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* User Email Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">User Email</label>
              <input
                type="text"
                value={filters.user_email}
                onChange={(e) => handleFilterChange('user_email', e.target.value)}
                placeholder="Search by email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Alert type="error" message={error} onClose={() => setError('')} />
        )}

        {/* Empty State */}
        {!loading && !error && filteredPayments.length === 0 && (
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No billing records found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {payments.length === 0 ? 'No payments have been made yet.' : 'No payments match the current filters.'}
            </p>
          </div>
        )}

        {/* Payment History List */}
        {!loading && !error && filteredPayments.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Payment History ({filteredPayments.length} {filteredPayments.length === 1 ? 'payment' : 'payments'})
              </h2>
            </div>

            <div className="divide-y divide-gray-200">
              {filteredPayments.map((payment) => (
                <div key={payment.id} className="hover:bg-gray-50 transition-colors">
                  {/* Main Payment Row */}
                  <div
                    className="px-6 py-4 cursor-pointer"
                    onClick={() => toggleExpanded(payment.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3">
                              <div className="flex flex-col">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {payment.user_email}
                                </p>
                                <p className="text-xs text-gray-500">
                                  User ID: {payment.user}
                                </p>
                              </div>
                              <span className="text-gray-500 text-sm">
                                - {getPaymentTypeDisplay(payment.payment_type)}
                              </span>
                              {payment.membership_details?.membership_type?.name && (
                                <span className="text-gray-500 text-sm">
                                  ({payment.membership_details.membership_type.name})
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                                {payment.status?.charAt(0).toUpperCase() + payment.status?.slice(1) || 'Unknown'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {formatDate(payment.payment_date || payment.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {formatAmount(payment.amount)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {payment.payment_method || 'Credit Card'}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg
                            className={`w-5 h-5 text-gray-400 transform transition-transform ${
                              expandedItems.has(payment.id) ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedItems.has(payment.id) && (
                    <div className="px-6 pb-4 border-t border-gray-100 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                        {/* User & Payment Details */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">User & Payment Details</h4>
                          <dl className="space-y-2">
                            <div>
                              <dt className="text-xs text-gray-500">User ID</dt>
                              <dd className="text-sm text-gray-900 font-mono">{payment.user}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">User Email</dt>
                              <dd className="text-sm text-gray-900">{payment.user_email}</dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Transaction ID</dt>
                              <dd className="text-sm text-gray-900 font-mono" title={payment.transaction_id}>
                                {payment.transaction_id
                                  ? `${payment.transaction_id.substring(0, 12)}...${payment.transaction_id.slice(-6)}`
                                  : 'N/A'
                                }
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Payment Method</dt>
                              <dd className="text-sm text-gray-900">
                                {payment.payment_method || 'Credit Card'}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Type</dt>
                              <dd className="text-sm text-gray-900">
                                {getPaymentTypeDisplay(payment.payment_type)}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Auto Renewal</dt>
                              <dd className="text-sm text-gray-900">
                                {payment.membership_details ? (
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    payment.membership_details.auto_renew
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {payment.membership_details.auto_renew ? 'Enabled' : 'Disabled'}
                                  </span>
                                ) : 'N/A'}
                              </dd>
                            </div>
                            {payment.failure_reason && (
                              <div>
                                <dt className="text-xs text-gray-500">Failure Reason</dt>
                                <dd className="text-sm text-red-600">{payment.failure_reason}</dd>
                              </div>
                            )}
                          </dl>
                        </div>

                        {/* Membership/Service Details */}
                        {payment.membership_details && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Membership Details</h4>
                            <dl className="space-y-2">
                              <div>
                                <dt className="text-xs text-gray-500">Plan</dt>
                                <dd className="text-sm text-gray-900">
                                  {payment.membership_details.membership_type?.name || 'N/A'}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-xs text-gray-500">Period Start</dt>
                                <dd className="text-sm text-gray-900">
                                  {payment.membership_details.current_period_start
                                    ? new Date(payment.membership_details.current_period_start + 'T00:00:00').toLocaleDateString()
                                    : 'N/A'}
                                </dd>
                              </div>
                              {payment.membership_details.current_period_end && (
                                <div>
                                  <dt className="text-xs text-gray-500">Period End</dt>
                                  <dd className="text-sm text-gray-900">
                                    {new Date(payment.membership_details.current_period_end + 'T00:00:00').toLocaleDateString()}
                                  </dd>
                                </div>
                              )}
                              <div>
                                <dt className="text-xs text-gray-500">Duration</dt>
                                <dd className="text-sm text-gray-900">
                                  {payment.membership_details.membership_type?.duration_months
                                    ? `${payment.membership_details.membership_type.duration_months} months`
                                    : 'N/A'}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-xs text-gray-500">Status</dt>
                                <dd className="text-sm text-gray-900">
                                  {payment.membership_details.status || 'N/A'}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        )}

                        {/* Admin Actions */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Admin Actions</h4>
                          <div className="space-y-2">
                            {payment.stripe_receipt_url && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(payment.stripe_receipt_url, '_blank');
                                }}
                                className="w-full inline-flex items-center justify-center px-3 py-2 border border-purple-300 shadow-sm text-xs font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                              >
                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                View Receipt
                              </button>
                            )}
                            {payment.transaction_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`https://dashboard.stripe.com/payments/${payment.transaction_id}`, '_blank');
                                }}
                                className="w-full inline-flex items-center justify-center px-3 py-2 border border-blue-300 shadow-sm text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                View in Stripe
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                alert('Invoice generation for admin will be implemented.');
                              }}
                              className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Generate Invoice
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                alert(`Viewing user profile for User ID: ${payment.user} (${payment.user_email})`);
                              }}
                              className="w-full inline-flex items-center justify-center px-3 py-2 border border-green-300 shadow-sm text-xs font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              View User Profile
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBillingHistory;